import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { LMDialogButtonKey } from '@utils/app.constants';
import { LMUniqueIdService } from '@utils/lm-unique-id.service';
import { LMContextService, LMMessageWrapperDialogRef, LMModalOpenService, LMModalWrapperService } from '@app/services';

export enum ConfirmationDialogResult {
  CONFIRMATION_CANCEL = 0,
  CONFIRMATION_OK,
  CONFIRMATION_OKTOALL
}

export interface ConfirmationMessageOptions {
  message: string;
  title?: string;         // TODO DPB this is not used
  okToAll?: boolean;
  alertMessage?: string;
  dialogTitle?: string;
}

@Injectable()
export class LMConfirmationMessageService {
  constructor(
    private lmUniqueIdService: LMUniqueIdService,
    private lmContextService: LMContextService,
    private lmModalWrapperService: LMModalWrapperService,
    private lmModalOpenService: LMModalOpenService
  ) {}

  private dialog: LMMessageWrapperDialogRef | null = null; // reference to the most recently opened dialog

  /**
   * Bring up the soho message service to display the confirmation message.
   * Args: message string, okToAll, title
   *
   */
  public promptConfirmationMessage(args: ConfirmationMessageOptions): Observable<ConfirmationDialogResult> {
    return new Observable<any>(observer => {
      const dialogId = args.dialogTitle === undefined ? 'ConfirmationRequired' : args.dialogTitle;
      const okToAll = args.okToAll || false;
      const message = args.message;
      let result = ConfirmationDialogResult.CONFIRMATION_CANCEL;

      const buttons: SohoModalButton[] = [
        {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CANCEL ]),
          text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: (e, modal) => {
            modal.close(true);
          }
        },
        {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.OK ]),
          text: Soho.Locale.translate(LMDialogButtonKey.OK),
          click: (e, modal) => {
            result = ConfirmationDialogResult.CONFIRMATION_OK;
            modal.close(true);
          },
          isDefault: true
        }
      ];

      if (okToAll) {
        buttons.splice(1, 0, {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.OKTOALL ]),
          text: Soho.Locale.translate(LMDialogButtonKey.OKTOALL),
          click: (e, modal) => {
            result = ConfirmationDialogResult.CONFIRMATION_OKTOALL;
            modal.close(true);
          }
        });
      }

      // this.lmBusyIndicatorService.hideBusyIndicator();

      const attributes: SohoAttributesArray = [ { name:  'data-automation-id',  value: 'lm-ConfirmationRequired-modal' } ];
      const messageOptions: SohoMessageOptions = {
        allowedTags: '<div>, <span>, <p>',
        message: this.buildMessage(args, message),
        buttons,
        title: args.title || Soho.Locale.translate('ConfirmationRequired'),
        attributes
      };

      // Show the confirmation message dialog.
      this.dialog = this.lmModalWrapperService
        .message(messageOptions)
        .open()
        .beforeClose(() => {
          if (this.lmContextService.isUnified() && this.dialog) {
            this.lmModalOpenService.removeModal(this.dialog.uuid);
          }
          this.dialog = null;
          observer.next(result);
          observer.complete();
          return true;
        });
    });
  }

  private buildMessage(options: ConfirmationMessageOptions, message:string): string {
    if (options.alertMessage) {
      return '<div class="notification alert lm-dialog-notification"><p>' + options.alertMessage + '</p></div><p>' + message + '</p>';
    } else {
      return message;
    }
  }
}
